import { useEffect, useState } from "react";

import { WindowType } from "../utils/types";

const isWindowType = (value: unknown): value is WindowType =>
  Object.values(WindowType).includes(value as WindowType);

// Older builds stored the whole `{ label, value }` option, so read either shape.
const parseStoredWindow = (raw: string | null): WindowType | null => {
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    const candidate =
      typeof parsed === "object" && parsed !== null && "value" in parsed
        ? parsed.value
        : parsed;
    return isWindowType(candidate) ? candidate : null;
  } catch {
    return null;
  }
};

const readStoredWindow = (key: string): WindowType | null => {
  try {
    return parseStoredWindow(localStorage.getItem(key));
  } catch {
    return null;
  }
};

export const usePersistedWindow = (key: string, fallback: WindowType) => {
  const [window, setWindow] = useState<WindowType>(
    () => readStoredWindow(key) ?? fallback,
  );

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(window));
    } catch {
      // Storage is a convenience; an unavailable one just means no memory.
    }
  }, [key, window]);

  return [window, setWindow] as const;
};
